import { useTranslator } from "@eo-locale/react";
import { Translator } from "@eo-locale/core";

import { useToast } from "@/storybook-components";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  fetchUserBudgetAllocations,
  removeBAContribution,
  saveBAContribution
} from "@/redux/features/brainstormers/thunk";

const useBudget = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { brainstormer, userAllocations } = useAppSelector(
    (state) => state.brainstormers
  );

  const { totalAllocated, remainingBudget } = userAllocations;
  const unitCap = brainstormer?.baCategoryConfigAttributes?.unitCap || 0;

  const formatBudget = (unit: number, unitCode: string) => {
    const unitCodeToLocaleMap: Record<string, string> = {
      USD: "en-US",
      GBP: "en-GB",
      CAD: "en-CA",
      AUD: "en-AU",
      NZD: "en-NZ"
    };

    const localeFromUnitCodeMap = unitCodeToLocaleMap[unitCode] || "en-US";

    const translator = new Translator(localeFromUnitCodeMap);
    if (unitCode === "PTS")
      return `${translator.formatNumber(unit)} ${unit >= 0 && unit <= 9 ? translate("budget.point") : translate("budget.points")}`;

    return translator.formatNumber(unit, {
      style: "currency",
      currency: unitCode || "USD",
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const addTotalFund = async (
    id: string,
    unitValue: number,
    onAfterSuccessToast?: () => void
  ) => {
    toast.closeAll();

    const result = await dispatch(
      saveBAContribution({ ideaId: id, unitValue })
    );
    if (result.type.endsWith("/fulfilled")) {
      await dispatch(fetchUserBudgetAllocations());
      toast({
        title: translate("budget.fundAdded"),
        status: "success",
        isClosable: true
      });
      onAfterSuccessToast?.();
    }

    if (result.type.endsWith("/rejected")) {
      const errorCode = result?.payload?.data?.errors?.[0]?.code;

      const errorMessage = {
        taken: translate("budget.fundAlreadyTaken"),
        less_than_or_equal_to: translate("budget.lessThanOrEqualTo", {
          totalBudget: formatBudget(
            unitCap,
            brainstormer?.baCategoryConfigAttributes?.unitCode || ""
          )
        })
      };

      toast({
        title:
          errorMessage[errorCode] ||
          translate("errors.sorrySomethingWentWrong"),
        status: "error",
        isClosable: true
      });
    }
  };

  const removeTotalFund = async (
    id: string,
    onAfterSuccessToast?: () => void
  ) => {
    toast.closeAll();

    const result = await dispatch(removeBAContribution({ ideaId: id }));
    if (result.type.endsWith("/fulfilled")) {
      await dispatch(fetchUserBudgetAllocations());

      toast({
        title: translate("budget.fundRemoved"),
        status: "warning",
        isClosable: true
      });
      onAfterSuccessToast?.();
    }

    if (result.type.endsWith("/rejected")) {
      toast.closeAll();

      toast({
        title: translate("errors.sorrySomethingWentWrong"),
        status: "error",
        isClosable: true
      });
    }
  };

  return {
    totalBudget: unitCap,
    remainingBudget,
    totalAllocated,
    formatBudget,
    addTotalFund,
    removeTotalFund
  };
};

export default useBudget;
